import { useFlyOutProductsBasedOnSelectedCloud } from '../../../../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import {
  faBell,
  faCancel,
  faCheck,
  faClone,
  faDeleteRight,
  faInfoCircle,
  faSend,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ButtonV2,
  ColumnActionWithMenuColors,
  ColumnActionWithMenuV2,
  ColumnMenuOption,
  LabraToolTipV2,
  ModalConfirmV2,
  ModalSizeVariants,
  VerticalDivider,
} from '@labrav/react-components'
import { useStyles } from './ChannelOfferHeaderActions.styles'
import {
  OfferStatus,
  PrivateOffer,
} from '../../../../../modules/privateOffer/reducer'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useMemo, useState } from 'react'
import { Typography } from '@mui/material'
import { useAuth } from '@labrav/react-components'
import { providerToAuthCloud } from '@labrav/common'
import { useDispatch, useSelector } from '../../../../../store'
import { clonePrivateOffer } from '../../../../../modules/privateOffer/action'
import { useNavigate, useParams } from 'react-router-dom'
import { useAccountId } from '@labrav/common'
import {
  type CloudMarketplace,
  type Product,
  ProductStatus,
} from '../../../../../modules/productsListing/types'
import { OfferReminderTheBuyer } from '../../../../../common/components/OfferReminderTheBuyer/OfferReminderTheBuyer'
import { OfferResendConfirmation } from '../../../../../common/components/OfferResendConfirmation/OfferResendConfirmation'
import { OfferHeaderIcon } from '../../../../OfferHeaderIcon/OfferHeaderIcon'
import {
  deleteCurrentCPPO,
  resendCPPO,
  sendReminderForCPPO,
  updateChannelPartnerPrivateOffer,
} from '../../../../../modules/channelPartnerPrivateOffer/action'
import { noop } from 'lodash'
import {
  copyToClipboard,
  LoadingTypes,
  isLoading as Loading,
  useCurrentCloudProvider,
} from '@labrav/common'
import {
  getDeleteOfferDisabledTooltip,
  type ChannelOfferHeaderActionsProps,
} from './helpers'

export { getDeleteOfferDisabledTooltip } from './helpers'
export type { ChannelOfferHeaderActionsProps } from './helpers'

export const ChannelOfferHeaderActions = ({
  channelPrivateOffer,
  crmOpportunityId,
  resaleAuthorizationId,
}: ChannelOfferHeaderActionsProps) => {
  const { classes } = useStyles()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const selectedCloudType = useCurrentCloudProvider()
  const {
    cloud: routeCloud,
    accountId: routeAccountId,
    productId,
  } = useParams<{
    cloud?: string
    accountId?: string
    productId: string
    id: string
  }>()
  const accountIdFromHook = useAccountId({ product: 'flyout' })
  const { loading: authLoading, canDo } = useAuth()
  const cloud = useCurrentCloudProvider()
  const canWriteMarketplace = useMemo(() => {
    return (
      !authLoading &&
      canDo({
        cloud: providerToAuthCloud(cloud.toLowerCase()),
        service: 'marketplace',
        action: 'create',
      })
    )
  }, [authLoading, canDo, cloud])

  const permissionMessageString =
    'This action is disabled because you have read-only access'

  const accountId = routeAccountId || accountIdFromHook

  const { products } = useFlyOutProductsBasedOnSelectedCloud()
  const selectedProductDetails = useMemo(() => {
    return (
      products.find(product => product.productId === productId) ||
      ({} as Product)
    )
  }, [products, productId])
  const { id, offerStatus } = channelPrivateOffer
  const [copiedOfferLink, setCopiedOfferLink] = useState(false)
  const [openCloneModal, setOpenCloneModal] = useState(false)
  const handleOpenCloneModal = () => {
    setOpenCloneModal(true)
  }
  const handleCloseCloneModal = () => {
    setOpenCloneModal(false)
  }
  const isLoading = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.GENERAL)
  )
  const isLoadingSubmit = useSelector<boolean>(state =>
    Loading(state.loading)(LoadingTypes.UPDATE_FLYOUT_OFFER)
  )
  const [openCancelModal, setOpenCancelModal] = useState(false)
  const handleCloseCancelModal = () => {
    setOpenCancelModal(false)
  }
  const handleOpenCancelModal = () => {
    setOpenCancelModal(true)
  }
  const [openReminderTheBuyerModal, setOpenReminderTheBuyerModal] =
    useState(false)

  const [
    openOfferResendConfirmationModal,
    setOpenOfferResendConfirmationModal,
  ] = useState(false)

  const isVisibleOfferLink = [
    OfferStatus.PRIVATE_OFFER_EMAIL_SENT,
    OfferStatus.PRIVATE_OFFER_VIEWED,
    OfferStatus.PRIVATE_OFFER_EXPIRED,
  ].includes(offerStatus || OfferStatus.PRIVATE_OFFER_EMAIL_SENT)

  const offerLinkTooltipPlacement = copiedOfferLink
    ? 'top'
    : offerStatus === OfferStatus.PRIVATE_OFFER_EXPIRED
      ? 'top-end'
      : 'top'

  const handleOpenReminderTheBuyerModal = () => {
    setOpenReminderTheBuyerModal(true)
  }

  const handleOpenOfferResendConfirmationModal = () => {
    setOpenOfferResendConfirmationModal(true)
  }

  const isVisibleCancel = [
    OfferStatus.PRIVATE_OFFER_EMAIL_SENT,
    OfferStatus.PRIVATE_OFFER_VIEWED,
  ].includes(offerStatus || OfferStatus.PRIVATE_OFFER_EMAIL_SENT)

  const isVisibleReminder = [
    OfferStatus.PRIVATE_OFFER_EMAIL_SENT,
    OfferStatus.PRIVATE_OFFER_VIEWED,
  ].includes(offerStatus || OfferStatus.PRIVATE_OFFER_EMAIL_SENT)

  const isVisibleMoreMenu = [
    OfferStatus.PRIVATE_OFFER_ACCEPTED,
    OfferStatus.PRIVATE_OFFER_FAILED,
    OfferStatus.PRIVATE_OFFER_EXPIRED,
    OfferStatus.PRIVATE_OFFER_RESTRCITED,
    OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS,
  ].includes(offerStatus || OfferStatus.PRIVATE_OFFER_ACCEPTED)

  const handleConfirmCancelModal = async () => {
    await dispatch(
      updateChannelPartnerPrivateOffer(
        channelPrivateOffer?.id,
        {
          offerStatus: OfferStatus.PRIVATE_OFFER_RESTRICTION_IN_PROGRESS,
        },
        crmOpportunityId,
        false,
        handleCloseCancelModal,
        resaleAuthorizationId,
        accountId,
        selectedCloudType
      )
    )
  }

  const [openDeleteModal, setOpenDeleteModal] = useState(false)

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false)
  }

  const handleOpenDeleteModal = () => {
    setOpenDeleteModal(true)
  }

  const handleConfirmDeleteModal = async () => {
    await dispatch(
      deleteCurrentCPPO(
        id as string,
        () => setOpenDeleteModal(false),
        accountId,
        selectedCloudType
      )
    )

    const cloudPath =
      routeCloud?.toLowerCase() || selectedCloudType.toLowerCase()

    const channelOffersListPath =
      routeCloud && accountId
        ? `/flyout/${cloudPath}/${accountId}/offers-authorizations/channel-offers`
        : '/flyout/offers-authorizations/channel-offers'

    navigate(channelOffersListPath)
  }

  const handleSelectOption = (option: ColumnMenuOption) => {
    if (option.key === 'delete-offer') {
      handleOpenDeleteModal()
    }
  }

  const copyOfferLink = async () => {
    const link = channelPrivateOffer?.cloudOfferUrl
    if (!link) {
      console.error('No offer link found')
      return
    }
    try {
      await copyToClipboard(link)
      setCopiedOfferLink(true)
      setTimeout(() => {
        setCopiedOfferLink(false)
      }, 2000)
    } catch (error) {
      console.error('Error copying URL to clipboard:', error)
    }
  }

  const handleRedirectCloneOffer = ({
    offerId,
    dimensionUpdate,
  }: {
    offerId?: string
    dimensionUpdate?: 'both' | 'product' | 'usage'
  }) => {
    const cloudPath =
      routeCloud?.toLowerCase() || selectedCloudType.toLowerCase()

    const privateOfferEditPath =
      routeCloud && accountId
        ? `/flyout/${cloudPath}/${accountId}/products-listing/${productId}/private-offers/${offerId}/edit`
        : `/flyout/products-listing/${productId}/private-offers/${offerId}/edit`
    const dimensionNotice = dimensionUpdate
      ? `?dimensionUpdate=${dimensionUpdate}`
      : ''

    navigate(`${privateOfferEditPath}${dimensionNotice}`)
  }
  const handleConfirmCloneModal = () => {
    if (!productId) {
      return
    }
    dispatch(
      clonePrivateOffer({
        partnerId: accountId || '',
        cloudMarketplace: selectedCloudType,
        productId: productId,
        crmOpportunityId,
        privateOffer: channelPrivateOffer as unknown as PrivateOffer,
        callBack: handleRedirectCloneOffer,
      })
    )
  }

  const handleSubmitReminder = (data: Record<string, unknown>) => {
    dispatch(
      sendReminderForCPPO({
        data: data,
        privateOfferId: channelPrivateOffer?.id,
        callBack: () => setOpenReminderTheBuyerModal(false),
        partnerId: accountId,
      })
    )
  }

  const handleSubmitResend = (data: Record<string, unknown>) => {
    dispatch(
      resendCPPO({
        data: data,
        privateOfferId: channelPrivateOffer?.id,
        callBack: () => setOpenOfferResendConfirmationModal(false),
        partnerId: accountId,
      })
    )
  }

  const hide = true

  return (
    <div className={classes.root} data-testid={'offer-header-actions'}>
      <div className={classes.leftContainer}>
        {!hide && (
          <LabraToolTipV2
            tooltipPlacement="bottom"
            description={
              !canWriteMarketplace
                ? permissionMessageString
                : selectedProductDetails.status ===
                    ProductStatus.PRODUCT_STATUS_RESTRICTED
                  ? 'Cannot clone offer for restricted products.'
                  : undefined
            }
          >
            <ButtonV2
              styleType="link"
              data-testid={'clone-offer'}
              onClick={handleOpenCloneModal}
              className={
                selectedProductDetails.status ===
                ProductStatus.PRODUCT_STATUS_RESTRICTED
                  ? classes.btnDisabled
                  : ''
              }
            >
              <OfferHeaderIcon icon={faClone} />
              Clone
            </ButtonV2>
          </LabraToolTipV2>
        )}
        {isVisibleOfferLink && (
          <>
            <LabraToolTipV2
              description={
                !canWriteMarketplace
                  ? permissionMessageString
                  : copiedOfferLink
                    ? 'Copied'
                    : offerStatus === OfferStatus.PRIVATE_OFFER_EXPIRED
                      ? 'This offer has expired. Extend the offer expiry date and then copy the offer link.'
                      : undefined
              }
              tooltipPlacement={offerLinkTooltipPlacement}
              icon={
                copiedOfferLink &&
                offerStatus !== OfferStatus.PRIVATE_OFFER_EXPIRED ? (
                  <FontAwesomeIcon
                    icon={faCheck as IconProp}
                    style={{ width: '12px', height: '12px' }}
                  />
                ) : undefined
              }
              tooltipPlacmentGap="0"
            >
              <span>
                <ButtonV2
                  data-testid={'offer-link'}
                  styleType="link"
                  onClick={copyOfferLink}
                  disabled={
                    !canWriteMarketplace ||
                    offerStatus === OfferStatus.PRIVATE_OFFER_EXPIRED
                  }
                  className={
                    offerStatus === OfferStatus.PRIVATE_OFFER_EXPIRED
                      ? classes.btnDisabled
                      : ''
                  }
                >
                  Offer link
                </ButtonV2>
              </span>
            </LabraToolTipV2>
          </>
        )}
        {isVisibleCancel && (
          <>
            <VerticalDivider height="24px" />
            <LabraToolTipV2
              tooltipPlacement="bottom"
              description={
                !canWriteMarketplace ? permissionMessageString : undefined
              }
            >
              <span>
                <ButtonV2
                  styleType="link"
                  data-testid={'offer-cancel'}
                  onClick={handleOpenCancelModal}
                  disabled={!canWriteMarketplace}
                >
                  <OfferHeaderIcon icon={faCancel} />
                  Cancel
                </ButtonV2>
              </span>
            </LabraToolTipV2>
          </>
        )}
        {isVisibleReminder && (
          <>
            <VerticalDivider height="24px" />
            <LabraToolTipV2
              tooltipPlacement="bottom"
              description={
                !canWriteMarketplace ? permissionMessageString : undefined
              }
            >
              <span>
                <ButtonV2
                  styleType="link"
                  data-testid={'remind-the-buyer'}
                  onClick={handleOpenReminderTheBuyerModal}
                  disabled={!canWriteMarketplace}
                >
                  <OfferHeaderIcon icon={faBell} />
                  Remind the buyers
                </ButtonV2>
              </span>
            </LabraToolTipV2>
          </>
        )}
        {offerStatus === OfferStatus.PRIVATE_OFFER_ACCEPTED && (
          <>
            <LabraToolTipV2
              tooltipPlacement="bottom"
              description={
                !canWriteMarketplace
                  ? permissionMessageString
                  : 'Resend offer confirmation email to the selected stakeholders.'
              }
            >
              <span>
                <ButtonV2
                  styleType="link"
                  data-testid={'resend-offer-acceptance-email'}
                  onClick={handleOpenOfferResendConfirmationModal}
                  disabled={!canWriteMarketplace}
                >
                  <OfferHeaderIcon icon={faSend} />
                  Resend offer acceptance email
                </ButtonV2>
              </span>
            </LabraToolTipV2>
            <LabraToolTipV2
              tooltipPlacement="bottom"
              description={
                'Resend offer confirmation email to the selected stakeholders.'
              }
            >
              <OfferHeaderIcon icon={faInfoCircle} color={'#6A777A'} />
            </LabraToolTipV2>
          </>
        )}
      </div>
      {isVisibleMoreMenu && (
        <ColumnActionWithMenuV2
          iconColor={ColumnActionWithMenuColors.BLUE}
          columnTitle={<div className={classes.moreMenu}>More</div>}
          menuOptions={[
            {
              key: 'delete-offer',
              label: 'Delete offer',

              disable:
                !canWriteMarketplace ||
                !(
                  offerStatus === OfferStatus.PRIVATE_OFFER_EXPIRED ||
                  offerStatus === OfferStatus.PRIVATE_OFFER_FAILED ||
                  offerStatus === OfferStatus.PRIVATE_OFFER_RESTRCITED
                ),

              disabledTooltip: !canWriteMarketplace
                ? permissionMessageString
                : getDeleteOfferDisabledTooltip(
                    channelPrivateOffer as never,
                    canWriteMarketplace
                  ),
            },
          ]}
          handleSelectOption={handleSelectOption}
        />
      )}
      <ModalConfirmV2
        title={'Cancel this private offer? '}
        description={
          <Typography className={classes.modalText}>
            <p>
              Your offer will be cancelled on the AWS Marketplace, and you will
              receive an email notification.
              <p>
                Please note that once cancelled, this offer cannot be restored.
              </p>
            </p>

            <p>Are you sure you want to cancel this offer?</p>
          </Typography>
        }
        open={openCancelModal}
        titleAcceptButton={'Confirm'}
        onAccept={handleConfirmCancelModal}
        acceptLoading={isLoading}
        onDecline={handleCloseCancelModal}
        variant={ModalSizeVariants.small}
        icon={
          <FontAwesomeIcon
            icon={faDeleteRight}
            style={{ width: '20px', height: '16px' }}
          />
        }
        hideCancelIcon={false}
        hideCancelButton={false}
        titleCancelButton={'Close'}
      />

      <ModalConfirmV2
        title={'Delete this private offer? '}
        description={
          <Typography className={classes.modalText}>
            <p>
              You will not be able to recover this offer or any information
              inside it once this offer is deleted.
            </p>

            <p>Are you sure you want to delete this offer?</p>
          </Typography>
        }
        open={openDeleteModal}
        titleAcceptButton={'Delete'}
        onAccept={handleConfirmDeleteModal}
        acceptLoading={isLoading}
        onDecline={handleCloseDeleteModal}
        variant={ModalSizeVariants.small}
        icon={
          <FontAwesomeIcon
            icon={faTrash}
            style={{ width: '14px', height: '16px' }}
          />
        }
        hideCancelIcon={false}
        hideCancelButton={false}
      />
      <OfferReminderTheBuyer
        cloudMarketplace={routeCloud as CloudMarketplace}
        partnerId={accountId || ''}
        offerId={channelPrivateOffer?.id}
        offerRecipients={channelPrivateOffer?.offerRecipients || []}
        openReminderTheBuyerModal={openReminderTheBuyerModal}
        setOpenReminderTheBuyerModal={setOpenReminderTheBuyerModal}
        handleSubmit={handleSubmitReminder}
      />
      <OfferResendConfirmation
        offerId={channelPrivateOffer?.id}
        offerStakeholders={channelPrivateOffer?.offerStakeholders}
        openOfferResendConfirmationModal={openOfferResendConfirmationModal}
        setOpenOfferResendConfirmationModal={
          setOpenOfferResendConfirmationModal
        }
        handleSubmit={handleSubmitResend}
        cloudMarketplace={routeCloud as CloudMarketplace}
        partnerId={accountId || ''}
      />
      <ModalConfirmV2
        title={'Clone private offer?'}
        content={
          <>
            <Typography className={classes.modalText}>
              <p>
                All data from this private offer will be copied to the new
                private offer form.
              </p>

              <p>
                <span className={classes.descriptionNote}>Note:</span> You can
                edit all information after cloning in the next step.
              </p>
            </Typography>
            <div className={classes.rowRightItem}>
              <ButtonV2
                data-testid="send-reminder"
                smallVariant
                styleType="outlined"
                onClick={handleCloseCloneModal}
              >
                Cancel
              </ButtonV2>
              <ButtonV2
                data-testid="send-reminder"
                smallVariant
                styleType="primary"
                onClick={handleConfirmCloneModal}
                loading={isLoadingSubmit}
              >
                Clone private offer
              </ButtonV2>
            </div>
          </>
        }
        open={openCloneModal}
        onDecline={handleCloseCloneModal}
        variant={ModalSizeVariants.small}
        icon={
          <FontAwesomeIcon
            icon={faClone}
            style={{ width: '14px', height: '16px' }}
          />
        }
        hideCancelIcon={false}
        hideCancelButton={true}
        hideAcceptButton={true}
        onAccept={noop}
      />
    </div>
  )
}
